import { EDITOR_DESKTOP_MEDIA_QUERY } from '@/components/app-editor/use-editor-chrome';

const ATTRIBUTE = 'data-onboarding-studio';

// Only if the navigation never starts a view transition (unsupported browser,
// skipped transition): long past the 0.5s animation in globals.css.
const FALLBACK_CLEANUP_MS = 3000;

/**
 * Below lg, the next `viewTransition: true` navigation presents the Korza
 * Studio like an iOS modal sheet (it rises from the bottom while the platform
 * step recedes behind it), or dismisses it the opposite way. Styles in
 * globals.css; no-op from lg, where the Studio is always on screen.
 *
 * The attribute stays until the root snapshot's animation ends: removed any
 * earlier, the running transition falls back to the default crossfade and
 * the previous page flashes back in.
 */
export function markStudioModalTransition(kind: 'present' | 'dismiss'): void {
  if (window.matchMedia(EDITOR_DESKTOP_MEDIA_QUERY).matches) return;
  const root = document.documentElement;
  root.setAttribute(ATTRIBUTE, kind);

  const cleanup = () => {
    root.removeAttribute(ATTRIBUTE);
    root.removeEventListener('animationend', onAnimationEnd);
    window.clearTimeout(fallback);
  };
  // The snapshots keep their end state (fill-mode both) until the browser
  // tears the transition down, which Safari does a frame or more after
  // `animationend`: wait for that, or the default crossfade restarts.
  const cleanupOnceTornDown = () => {
    const running = document
      .getAnimations()
      .some((animation) =>
        (animation.effect as KeyframeEffect | null)?.pseudoElement?.startsWith(
          '::view-transition'
        )
      );
    if (running) requestAnimationFrame(cleanupOnceTornDown);
    else cleanup();
  };
  const onAnimationEnd = (e: AnimationEvent) => {
    // Not the app icon's group, which ends first (0.45s).
    if (/^::view-transition-(old|new)\(root\)$/.test(e.pseudoElement)) {
      cleanupOnceTornDown();
    }
  };
  root.addEventListener('animationend', onAnimationEnd);
  const fallback = window.setTimeout(cleanup, FALLBACK_CLEANUP_MS);
}
